import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { PrintPageComponent } from '@next/core-lib/components';
import { TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { AgrUser, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, map, skipWhile } from 'rxjs';

import { BrandingLogoSrc } from 'src/app/branding/branding.model';
import { TenantAwareTranslationService } from 'src/app/shared/services/tenant-aware-translation.service';

import {
  DeliveryPrintRow,
  FinalSetupPrintRow,
  FixedPriceCorrectionPrintRow,
} from '../final-setup/models/final-setup.model';

@Component({
  selector: 'app-final-setup-print',
  templateUrl: './final-setup-print.component.html',
  styleUrls: ['./final-setup-print.component.scss'],
  imports: [NgIf, PrintPageComponent, NgFor, AsyncPipe, TranslatePipe, AgrDatePipe, AgrNumberPipe],
})
export class FinalSetupPrintComponent implements OnInit {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly brandingService = inject(BrandingService);
  private readonly tenantTranslation = inject(TenantAwareTranslationService);

  @Input() feedDeliveriesAggregate$: Observable<FinalSetupPrintRow>;
  @Input() feedDeliveries$: Observable<DeliveryPrintRow[]>;
  @Input() feedDeliveriesTotal$: Observable<DeliveryPrintRow>;
  @Input() discounts$: Observable<FinalSetupPrintRow[]>;
  @Input() chickDeliveries$: Observable<FinalSetupPrintRow[]>;
  @Input() departures$: Observable<FinalSetupPrintRow[]>;
  @Input() totalsExcludingDepartures$: Observable<FinalSetupPrintRow>;
  @Input() totals$: Observable<FinalSetupPrintRow>;
  @Input() showTotalsExcludingDepartures$: Observable<boolean>;
  @Input() additionalInvoices$: Observable<FinalSetupPrintRow[]>;
  @Input() additionalInvoicesTotal$: Observable<FinalSetupPrintRow>;
  @Input() fixedPriceCorrection$: Observable<FixedPriceCorrectionPrintRow | undefined>;

  public location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  public user$: Observable<AgrUser> = this.store.select(UserSelectors.selectUser);
  public totalAmountIncludingVatPrint$: Observable<number>;
  public totalIsPositive$: Observable<boolean>;
  public footerText$: Observable<string>;
  public companyName$: Observable<string>;
  public companyEmail$: Observable<string>;
  public companyPhone$: Observable<string>;

  public currentDate: Date = new Date();

  showAgrifirmLogo = true;
  customizedLogoSrc: string = BrandingLogoSrc.PoultryNextLogo;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    if (this.brandingService.isTenant('Agravis')) {
      this.customizedLogoSrc = BrandingLogoSrc.AgravisLogo;
      this.showAgrifirmLogo = false;
    }
  }

  ngOnInit(): void {
    this.totalAmountIncludingVatPrint$ = this.totals$.pipe(
      skipWhile((totals) => !totals),
      map((totals) => Math.abs(totals.amountIncludingVat ?? 0)),
    );

    this.totalIsPositive$ = this.totals$.pipe(
      skipWhile((totals) => !totals),
      map((totals) => totals.amountIncludingVat >= 0),
    );

    this.footerText$ = this.totals$.pipe(
      skipWhile((totals) => !totals),
      map(() => this.tenantTranslation.translate('final-setup.print-footer')),
    );

    this.companyName$ = this.totals$.pipe(
      skipWhile((totals) => !totals),
      map(() => this.tenantTranslation.translate('final-setup.print-company-name')),
    );

    this.companyEmail$ = this.totals$.pipe(
      skipWhile((totals) => !totals),
      map(() => this.tenantTranslation.translate('final-setup.print-company-email')),
    );

    this.companyPhone$ = this.totals$.pipe(
      skipWhile((totals) => !totals),
      map(() => this.tenantTranslation.translate('final-setup.print-company-phone')),
    );
  }
}
